import { Typography } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { SingleTargetSelector } from '@/components/common/SingleTargetSelector';
import type { MinimalTarget } from '@/generated/model';

import { useDiagramSeriesOptions } from '../../hooks/useDiagramSeriesOptions';
import { useDiagramSlownessReport } from '../../monitoring/hooks/useDiagramSlownessReport';
import { useSeriesSelectionPreferences } from '../../hooks/useSeriesSelectionPreferences';
import { useTimeSeriesChartModel } from '../../hooks/useTimeSeriesChartModel';
import { useTimeSeriesChartState } from '../../hooks/useTimeSeriesChartState';
import { TimeSeriesChartErrorState } from '../time-series-chart-error-state/TimeSeriesChartErrorState';
import { TimeSeriesChartResultPanel } from '../time-series-chart-result-panel/TimeSeriesChartResultPanel';
import { TimeSeriesChartSelectionPanel } from '../time-series-chart-selection-panel/TimeSeriesChartSelectionPanel';
import { TimeSeriesChartToolbar } from '../time-series-chart-toolbar/TimeSeriesChartToolbar';
import {
  ResultArea,
  ResultHeader,
  SelectorArea,
  SelectorViewport,
  TimeSeriesChartRoot,
} from './TimeSeriesChartDesktop.style';

type TimeSeriesChartResultHeaderProps = {
  targetName: string | undefined;
};

function TimeSeriesChartResultHeader({ targetName }: TimeSeriesChartResultHeaderProps) {
  const { t } = useTranslation();

  return (
    <ResultHeader>
      <Typography variant="h6" color="text.primary" data-testid="time-series-chart-result-title">
        {t('timeSeriesChart.title')}
      </Typography>
      {targetName && (
        <Typography variant="h6" component="span" color="text.secondary" data-testid="time-series-chart-result-target">
          ({targetName})
        </Typography>
      )}
    </ResultHeader>
  );
}

export function TimeSeriesChartDesktop() {
  const { t } = useTranslation();
  const state = useTimeSeriesChartState();
  const selection = useSeriesSelectionPreferences(state.targetId);
  const seriesOptions = useDiagramSeriesOptions({
    targetId: state.targetId,
    timeWindow: state.timeWindow,
    selectedSeriesIds: selection.selectedSeriesIds,
  });
  const chartModel = useTimeSeriesChartModel({
    targetId: state.targetId,
    timeWindow: state.timeWindow,
    requestedSeriesIds: selection.selectedSeriesIds,
    series: seriesOptions.series,
    selectedSeriesIds: seriesOptions.validSelectedSeriesIds,
    highlightedSeriesIds: selection.highlightedSeriesIds,
    getSeriesColor: seriesOptions.getSeriesColor,
  });

  // Elements, not refs: the areas mount only after a target error clears, and the watchers must follow.
  const [selectorArea, setSelectorArea] = useState<HTMLElement | null>(null);
  const [resultArea, setResultArea] = useState<HTMLElement | null>(null);

  useDiagramSlownessReport({
    targetId: state.targetId,
    timeWindow: state.timeWindow,
    requestedSeriesIds: selection.selectedSeriesIds,
    chartElement: resultArea,
    selectorElement: selectorArea,
  });

  // The chart always has navigation, so the hint shows whenever a chart is on screen.
  const isNavigationHintVisible = chartModel.status.type === 'ready';

  function handleTargetChange(target: MinimalTarget | undefined) {
    if (target) {
      state.selectTarget(target);
      return;
    }

    state.clearTarget();
  }

  function handleDateChange(from: Date, to: Date) {
    state.changeDates({ from, to });
  }

  if (state.isTargetError) {
    return (
      <TimeSeriesChartRoot data-qa-element-id="time-series-chart">
        <TimeSeriesChartErrorState
          message={t('error.api.unknown')}
          isRetrying={state.isTargetRetrying}
          onRetry={state.retryTarget}
          data-qa-element-id="time-series-chart-target-error"
        />
      </TimeSeriesChartRoot>
    );
  }

  return (
    <TimeSeriesChartRoot data-qa-element-id="time-series-chart">
      <SelectorArea ref={setSelectorArea}>
        <SelectorViewport>
          {state.isChannelSelectorOpen ? (
            <TimeSeriesChartSelectionPanel
              seriesGroups={seriesOptions.groups}
              getSeriesColor={seriesOptions.getSeriesColor}
              selectedDates={state.selectedDates}
              onDateChange={handleDateChange}
              selectedSeriesIds={seriesOptions.validSelectedSeriesIds}
              onSelectedSeriesIdsChange={selection.changeSelectedSeries}
              isSeriesLoading={seriesOptions.isLoading}
              isSeriesError={seriesOptions.isError}
              onSeriesRetry={seriesOptions.retry}
              onBack={state.closeChannelSelector}
            />
          ) : (
            <SingleTargetSelector
              selectedTarget={state.selectedTarget}
              setSelectedTarget={handleTargetChange}
              searchText={state.searchText}
              setSearchText={state.changeSearchText}
            />
          )}
        </SelectorViewport>
      </SelectorArea>
      <ResultArea ref={setResultArea} data-qa-element-id="time-series-chart-result-panel">
        {state.targetId !== undefined && <TimeSeriesChartResultHeader targetName={state.selectedTarget?.name} />}
        <TimeSeriesChartToolbar
          selectedSeries={seriesOptions.selectedSeries}
          highlightedSeriesIds={selection.highlightedSeriesIds}
          getSeriesColor={seriesOptions.getSeriesColor}
          onHighlightToggle={selection.toggleHighlight}
          onDeselect={selection.deselectSeries}
          isNavigationHintVisible={isNavigationHintVisible}
        />
        <TimeSeriesChartResultPanel
          chart={chartModel.chart}
          previewSeriesId={chartModel.previewSeriesId}
          timeDomain={state.timeDomain}
          status={chartModel.status}
          onRetry={chartModel.retry}
        />
      </ResultArea>
    </TimeSeriesChartRoot>
  );
}
